/**
 * @file
 * events-search.js
 *
 * AJAX-driven Events listing page behavior.
 *
 * ── DOM contract (nebraska-events-page.html.twig) ──────────────────────────
 *   [data-events-search]          – root wrapper for the entire page section
 *   [data-events-filter-toggle]  – mobile "Filter Results" button
 *   [data-events-filters-overlay]– sidebar / mobile overlay panel
 *   [data-events-applied-chips]  – JS-managed applied chips region (hidden initially)
 *   [data-events-chips-list]     – chips list inside the applied chips region
 *   [data-events-clear-all]      – Clear All button
 *   [data-events-results]        – results container (innerHTML replaced by AJAX;
 *                                  aria-busy="true" during in-flight fetch fades it)
 *
 * ── Query param names ──────────────────────────────────────────────────────
 *   keywords   string
 *   category[] array of term IDs
 *   type[]     array of term IDs
 *   start_date mm/dd/yyyy or yyyy-mm-dd
 *   end_date   mm/dd/yyyy or yyyy-mm-dd
 *   page       int (0-based)
 *
 * ── Date trigger rules ────────────────────────────────────────────────────
 *   Picker selection  → filterDateInput:change event → immediate fetch.
 *   Manual entry      → keydown Enter → validate format → fetch if valid,
 *                       show inline error if invalid.
 */

/* global CSS, drupalSettings */
/* eslint-disable no-param-reassign */

(function ($, Drupal, once) {
  'use strict';

  // ── Date format validation (client-side, matches PHP parseDate) ────────────

  var DATE_REGEXPS = [
    /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/\d{4}$/, // mm/dd/yyyy
    /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,      // yyyy-mm-dd
  ];

  function isValidDateFormat(str) {
    return DATE_REGEXPS.some(function (re) {
      return re.test(str.trim());
    });
  }

  function debounce(fn, delay) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  // ── Behavior ──────────────────────────────────────────────────────────────

  Drupal.behaviors.eventsSearch = {
    attach: function (context) {
      once('eventsSearch', '[data-events-search]', context).forEach(function (root) {

        var resultsContainer = root.querySelector('[data-events-results]');
        var appliedChipsRegion = root.querySelector('[data-events-applied-chips]');
        var chipsListEl = root.querySelector('[data-events-chips-list]');
        var filterToggle = root.querySelector('[data-events-filter-toggle]');
        var filtersOverlay = root.querySelector('[data-events-filters-overlay]');

        // Tracks the most recent in-flight AJAX request so a newer request can
        // cancel it. Without this, a slower earlier request can resolve after a
        // faster later request and overwrite the DOM with stale results — e.g.
        // filter-change → Clear-All would occasionally "revert" the cleared
        // state back to the previous filtered results.
        var currentAbort = null;

        if (!resultsContainer) {
          return;
        }

        // ── Mobile filter overlay ────────────────────────────────────────────

        /**
         * Returns the current bottom edge of the site header (px from viewport
         * top) so the overlay and scrim can start just below the navbar.
         * Falls back to 0 if the header is not found or already scrolled away.
         */
        function getHeaderOffset() {
          var header = document.querySelector('header.o-nebraska-header');
          if (!header) { return 0; }
          return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }

        function openFiltersOverlay() {
          // Measure header height at open-time so sticky/scroll state is accurate.
          root.style.setProperty('--events-page-top-offset', getHeaderOffset() + 'px');
          root.classList.add('t-events-page--filters-open');
          if (filterToggle) {
            filterToggle.setAttribute('aria-expanded', 'true');
          }
          document.body.classList.add('t-events-page--scroll-lock');
        }

        function closeFiltersOverlay() {
          root.classList.remove('t-events-page--filters-open');
          if (filterToggle) {
            filterToggle.setAttribute('aria-expanded', 'false');
          }
          document.body.classList.remove('t-events-page--scroll-lock');
        }

        if (filterToggle) {
          filterToggle.addEventListener('click', openFiltersOverlay);
        }

        // "Apply Filters" button in the filters panel footer closes overlay + fetches.
        if (filtersOverlay) {
          var applyBtn = filtersOverlay.querySelector('.o-filters-panel__footer .a-button');
          if (applyBtn) {
            applyBtn.addEventListener('click', function (e) {
              e.preventDefault();
              closeFiltersOverlay();
              var state = readState();
              state.page = 0;
              fetchResults(state, true);
            });
          }
        }

        // Escape key closes the overlay.
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && root.classList.contains('t-events-page--filters-open')) {
            closeFiltersOverlay();
          }
        });

        // ── State helpers ────────────────────────────────────────────────────

        /**
         * Reads current control values from the DOM and returns a state object.
         */
        function readState() {
          var state = {
            keywords: '',
            category: [],
            type: [],
            start_date: '',
            end_date: '',
            page: 0,
          };

          var kwInputs = root.querySelectorAll('[data-events-keywords]');
          if (kwInputs.length) {
            state.keywords = kwInputs[0].value.trim();
          }

          root.querySelectorAll('input[name="category[]"]:checked').forEach(function (cb) {
            state.category.push(cb.value);
          });

          root.querySelectorAll('input[name="type[]"]:checked').forEach(function (cb) {
            state.type.push(cb.value);
          });

          var startInput = root.querySelector('[name="start_date"]');
          if (startInput) {
            state.start_date = startInput.value.trim();
          }

          var endInput = root.querySelector('[name="end_date"]');
          if (endInput) {
            state.end_date = endInput.value.trim();
          }

          return state;
        }

        /**
         * Serializes state to URLSearchParams, matching PHP's expected param names.
         */
        function stateToParams(state) {
          var params = new URLSearchParams();
          if (state.keywords) {
            params.set('keywords', state.keywords);
          }
          state.category.forEach(function (v) {
            params.append('category[]', v);
          });
          state.type.forEach(function (v) {
            params.append('type[]', v);
          });
          if (state.start_date) {
            params.set('start_date', state.start_date);
          }
          if (state.end_date) {
            params.set('end_date', state.end_date);
          }
          if (state.page > 0) {
            params.set('page', String(state.page));
          }
          return params;
        }

        /**
         * Builds a canonical /events URL from state.
         */
        function urlFromState(state) {
          var qs = stateToParams(state).toString();
          return qs ? '/events?' + qs : '/events';
        }

        /**
         * Restores control values from the current location.search (used on popstate).
         */
        function restoreFromUrl() {
          var params = new URLSearchParams(location.search);

          root.querySelectorAll('[data-events-keywords]').forEach(function (inp) {
            inp.value = params.get('keywords') || '';
          });

          root.querySelectorAll('input[name="category[]"]').forEach(function (cb) {
            cb.checked = params.getAll('category[]').indexOf(cb.value) !== -1;
          });

          root.querySelectorAll('input[name="type[]"]').forEach(function (cb) {
            cb.checked = params.getAll('type[]').indexOf(cb.value) !== -1;
          });

          var startInput = root.querySelector('[name="start_date"]');
          if (startInput) {
            startInput.value = params.get('start_date') || '';
          }

          var endInput = root.querySelector('[name="end_date"]');
          if (endInput) {
            endInput.value = params.get('end_date') || '';
          }
        }

        // ── Loading state ────────────────────────────────────────────────────
        // No visible spinner — instead, the results container fades to ~40%
        // opacity while aria-busy="true" (CSS rule in events-page.scss).

        function setLoading() {
          resultsContainer.setAttribute('aria-busy', 'true');
        }

        function clearLoading() {
          resultsContainer.removeAttribute('aria-busy');
        }

        // ── Applied chips (JS-managed region) ────────────────────────────────

        function getLabelForFilter(inputName, value) {
          var selector = 'input[name="' + inputName + '"][value="' + CSS.escape(value) + '"]';
          var cb = root.querySelector(selector);
          if (cb) {
            var label = root.querySelector('label[for="' + cb.id + '"]');
            if (label) {
              return label.textContent.trim();
            }
          }
          return value;
        }

        function buildSingleChipHtml(label, filterType, filterValue) {
          var escapedLabel = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          var removeLabel = Drupal.t('Remove @label', { '@label': label });
          var escapedValue = String(filterValue)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;');
          return (
            '<span class="a-filter-chip">' +
            '<span class="a-filter-chip__label">' + escapedLabel + '</span>' +
            '<button class="a-filter-chip__remove" type="button"' +
            ' aria-label="' + removeLabel + '"' +
            ' data-filter-type="' + filterType + '"' +
            ' data-filter-value="' + escapedValue + '">' +
            '<svg aria-hidden="true" focusable="false" class="a-filter-chip__remove-icon"' +
            ' width="16" height="16" viewBox="0 0 24 24" fill="none"' +
            ' stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
            '<line x1="18" y1="6" x2="6" y2="18"/>' +
            '<line x1="6" y1="6" x2="18" y2="18"/>' +
            '</svg>' +
            '</button>' +
            '</span>'
          );
        }

        function updateAppliedChips(state) {
          if (!appliedChipsRegion || !chipsListEl) {
            return;
          }

          var chips = [];

          if (state.keywords) {
            chips.push(buildSingleChipHtml(Drupal.t('Search: @keywords', { '@keywords': state.keywords }), 'keywords', state.keywords));
          }
          state.category.forEach(function (tid) {
            chips.push(buildSingleChipHtml(getLabelForFilter('category[]', tid), 'category', tid));
          });
          state.type.forEach(function (tid) {
            chips.push(buildSingleChipHtml(getLabelForFilter('type[]', tid), 'type', tid));
          });
          if (state.start_date) {
            chips.push(buildSingleChipHtml(Drupal.t('From: @date', { '@date': state.start_date }), 'start_date', state.start_date));
          }
          if (state.end_date) {
            chips.push(buildSingleChipHtml(Drupal.t('Until: @date', { '@date': state.end_date }), 'end_date', state.end_date));
          }

          if (chips.length === 0) {
            appliedChipsRegion.setAttribute('hidden', '');
            chipsListEl.innerHTML = '';
          } else {
            appliedChipsRegion.removeAttribute('hidden');
            chipsListEl.innerHTML = chips.join('');
          }
        }

        // ── Accordion row selected-bar sync ──────────────────────────────────
        /**
         * Reflects the active-filter state onto each mobile accordion row trigger.
         * For each .m-filters-accordion-row, inspects its content region for:
         *   – any checked checkbox, or
         *   – any text input (date fields) with a non-empty trimmed value.
         * Adds the --selected modifier class and injects / removes the 6px red
         * left-bar span so the mobile trigger visually indicates active filters.
         */
        function updateAccordionRowSelectedStates() {
          root.querySelectorAll('.m-filters-accordion-row').forEach(function (row) {
            var content = row.querySelector('.m-filters-accordion-row__content');
            if (!content) { return; }

            var hasActive = !!(
              content.querySelector('input[type="checkbox"]:checked') ||
              Array.prototype.some.call(
                content.querySelectorAll('input[type="text"]'),
                function (inp) { return inp.value.trim() !== ''; }
              )
            );

            var header = row.querySelector('.m-filters-accordion-row__header');
            if (!header) { return; }

            if (hasActive) {
              row.classList.add('m-filters-accordion-row--selected');
              if (!header.querySelector('.m-filters-accordion-row__selected-bar')) {
                var bar = document.createElement('span');
                bar.className = 'm-filters-accordion-row__selected-bar';
                bar.setAttribute('aria-hidden', 'true');
                header.insertBefore(bar, header.firstChild);
              }
            } else {
              row.classList.remove('m-filters-accordion-row--selected');
              var existingBar = header.querySelector('.m-filters-accordion-row__selected-bar');
              if (existingBar) {
                existingBar.parentNode.removeChild(existingBar);
              }
            }
          });
        }

        // ── Date input error state ────────────────────────────────────────────

        function setDateError(inputEl, message) {
          var wrapper = inputEl.closest('[data-date-picker]');
          if (wrapper) {
            wrapper.classList.add('m-filter-date-input--error');
          }
          inputEl.setAttribute('aria-invalid', 'true');
          var errorId = inputEl.id + '-date-err';
          var existing = document.getElementById(errorId);
          if (!existing) {
            var errEl = document.createElement('p');
            errEl.id = errorId;
            errEl.className = 'm-filter-date-input__error-msg';
            errEl.setAttribute('role', 'alert');
            errEl.textContent = message;
            if (wrapper) {
              wrapper.appendChild(errEl);
            }
          }
          var desc = inputEl.getAttribute('aria-describedby') || '';
          if (desc.indexOf(errorId) === -1) {
            inputEl.setAttribute('aria-describedby', (desc + ' ' + errorId).trim());
          }
        }

        function clearDateError(inputEl) {
          var wrapper = inputEl.closest('[data-date-picker]');
          if (wrapper) {
            wrapper.classList.remove('m-filter-date-input--error');
          }
          inputEl.removeAttribute('aria-invalid');
          var errorId = inputEl.id + '-date-err';
          var errEl = document.getElementById(errorId);
          if (errEl) {
            errEl.parentNode.removeChild(errEl);
          }
          var desc = (inputEl.getAttribute('aria-describedby') || '')
            .split(' ')
            .filter(function (s) { return s !== errorId; })
            .join(' ');
          if (desc) {
            inputEl.setAttribute('aria-describedby', desc);
          } else {
            inputEl.removeAttribute('aria-describedby');
          }
        }

        // ── Fetch results ────────────────────────────────────────────────────

        /**
         * Fetches /events/results, injects the HTML, updates URL, and chips.
         *
         * @param {object} state  Serialized filter state.
         * @param {boolean} push  true = pushState, false = replaceState.
         */
        function fetchResults(state, push, focusOnComplete) {
          var qs = stateToParams(state).toString();
          setLoading();

          // Cancel any in-flight request so its late-arriving response cannot
          // overwrite the DOM with stale results.
          if (currentAbort) {
            currentAbort.abort();
          }
          var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
          currentAbort = controller;

          fetch('/events/results' + (qs ? '?' + qs : ''), {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: controller ? controller.signal : undefined,
          })
            .then(function (response) {
              if (!response.ok) {
                throw new Error('HTTP ' + response.status);
              }
              return response.text();
            })
            .then(function (html) {
              resultsContainer.innerHTML = html;
              // Let Drupal re-process behaviors on injected markup (e.g., pager).
              if (Drupal.attachBehaviors) {
                Drupal.attachBehaviors(resultsContainer, drupalSettings);
              }
              updateAppliedChips(state);
              updateAccordionRowSelectedStates();
              var newUrl = urlFromState(state);
              if (push) {
                history.pushState({ eventsState: state }, '', newUrl);
              } else {
                history.replaceState({ eventsState: state }, '', newUrl);
              }
              clearLoading();
              // Scroll results into view on narrow viewports OR when we are
              // explicitly navigating (pagination). Desktop filter changes
              // don't scroll to avoid jumping the user mid-interaction.
              if (window.innerWidth < 1024 || focusOnComplete) {
                resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
              // Move keyboard focus to results only when explicitly requested
              // (e.g. after a pager click). Filter tweaks don't focus-steal.
              if (focusOnComplete) {
                if (!resultsContainer.hasAttribute('tabindex')) {
                  resultsContainer.setAttribute('tabindex', '-1');
                }
                resultsContainer.focus({ preventScroll: true });
              }
            })
            .catch(function (err) {
              // An aborted fetch is expected whenever a newer request supersedes
              // this one — leave the loading state alone (the newer request
              // owns it) and leave the DOM untouched.
              if (err && err.name === 'AbortError') {
                return;
              }
              clearLoading();
              var message = Drupal.t('Something went wrong while loading results. Please try again.');
              resultsContainer.innerHTML =
                '<div class="o-events-results o-events-results--error">' +
                '<p class="o-events-results__error-msg">' + message + '</p>' +
                '</div>';
            });
        }

        function triggerSearch(push) {
          fetchResults(readState(), push !== false);
        }

        // ── Breakpoint helper ─────────────────────────────────────────────────
        // Matches the SCSS `md` breakpoint (992px).  Used to suppress auto-fetch
        // on mobile: filter changes there only update visual state; the actual
        // search is deferred until the user taps "Apply filters".
        function isMobile() {
          return window.innerWidth < 992;
        }

        // ── Event listeners ──────────────────────────────────────────────────

        // Keyword search — handle submit button click and Enter key.
        // There may be two [data-events-keywords] inputs (one in the mobile bar,
        // one inside the desktop filter panel). Both are wired identically; typing
        // in one syncs the other so readState() always reads a consistent value.
        function triggerKeywordSearch() {
          var state = readState();
          state.page = 0;
          fetchResults(state, true);
        }

        // Sync all keyword inputs from the one the user just typed into.
        function syncKwInputs(sourceInput) {
          root.querySelectorAll('[data-events-keywords]').forEach(function (inp) {
            if (inp !== sourceInput) {
              inp.value = sourceInput.value;
            }
          });
        }

        root.querySelectorAll('.m-filter-search__form').forEach(function (searchRegion) {
          // Submit button click.
          var searchBtn = searchRegion.querySelector('.m-filter-search__button');
          if (searchBtn) {
            searchBtn.addEventListener('click', function () {
              triggerKeywordSearch();
            });
          }

          // Enter key + sync on each keyword input inside this search region.
          var kwInput = searchRegion.querySelector('[data-events-keywords]');
          if (kwInput) {
            kwInput.addEventListener('input', function () {
              syncKwInputs(kwInput);
            });
            kwInput.addEventListener('keydown', function (e) {
              if (e.key === 'Enter') {
                e.preventDefault();
                syncKwInputs(kwInput);
                triggerKeywordSearch();
              }
            });
          }
        });

        // Category checkboxes.
        root.querySelectorAll('input[name="category[]"]').forEach(function (cb) {
          cb.addEventListener('change', function () {
            updateAccordionRowSelectedStates();
            if (isMobile()) { return; }
            var state = readState();
            state.page = 0;
            fetchResults(state, true);
          });
        });

        // Type checkboxes.
        root.querySelectorAll('input[name="type[]"]').forEach(function (cb) {
          cb.addEventListener('change', function () {
            updateAccordionRowSelectedStates();
            if (isMobile()) { return; }
            var state = readState();
            state.page = 0;
            fetchResults(state, true);
          });
        });

        // Date picker — fires immediately when a date is selected via the calendar.
        root.addEventListener('filterDateInput:change', function (e) {
          var input = e.detail && e.detail.input;
          if (input) {
            clearDateError(input);
          }
          updateAccordionRowSelectedStates();
          if (isMobile()) { return; }
          var state = readState();
          state.page = 0;
          fetchResults(state, true);
        });

        // Date inputs — manual typing: trigger on Enter.
        ['start_date', 'end_date'].forEach(function (fieldName) {
          var inp = root.querySelector('[name="' + fieldName + '"]');
          if (!inp) {
            return;
          }
          inp.addEventListener('keydown', function (e) {
            if (e.key !== 'Enter') {
              return;
            }
            e.preventDefault();
            var val = inp.value.trim();
            if (val === '' || isValidDateFormat(val)) {
              clearDateError(inp);
              updateAccordionRowSelectedStates();
              if (isMobile()) { return; }
              var state = readState();
              state.page = 0;
              fetchResults(state, true);
            } else {
              setDateError(inp, Drupal.t('Invalid date format. Please use mm/dd/yyyy.'));
            }
          });
          // Clear error on input change after a prior validation failure.
          inp.addEventListener('input', function () {
            if (inp.getAttribute('aria-invalid') === 'true') {
              clearDateError(inp);
            }
          });
        });

        // Pager links inside the results container (event delegation).
        resultsContainer.addEventListener('click', function (e) {
          var link = e.target.closest('a[href]');
          if (!link) {
            return;
          }
          // Intercept only links that carry a page param (Drupal pager links).
          var href = link.getAttribute('href') || '';
          if (!/[?&]page=/.test(href)) {
            return;
          }
          e.preventDefault();
          var url = new URL(link.href, location.origin);
          var page = parseInt(url.searchParams.get('page') || '0', 10);
          var state = readState();
          state.page = isNaN(page) ? 0 : page;
          fetchResults(state, true, true);
        });

        // Applied chip remove buttons and Clear All (both delegated from root).
        root.addEventListener('click', function (e) {
          var clearBtn = e.target.closest('[data-events-clear-all]');
          if (clearBtn) {
            e.preventDefault();
            root.querySelectorAll('input[name="category[]"], input[name="type[]"]').forEach(function (cb) {
              cb.checked = false;
            });
            ['start_date', 'end_date'].forEach(function (n) {
              var inp = root.querySelector('[name="' + n + '"]');
              if (inp) {
                inp.value = '';
                clearDateError(inp);
                var pickerWrapper = inp.closest('[data-date-picker]');
                if (pickerWrapper && pickerWrapper._flatpickr) {
                  pickerWrapper._flatpickr.clear();
                }
              }
            });
            root.querySelectorAll('[data-events-keywords]').forEach(function (kw) {
              kw.value = '';
            });
            closeFiltersOverlay();
            var state = readState();
            state.page = 0;
            fetchResults(state, true);
            return;
          }

          var removeBtn = e.target.closest('[data-filter-type]');
          if (!removeBtn) {
            return;
          }
          e.preventDefault();
          var filterType = removeBtn.getAttribute('data-filter-type');
          var filterValue = removeBtn.getAttribute('data-filter-value');

          switch (filterType) {
            case 'category': {
              var cbCat = root.querySelector('input[name="category[]"][value="' + CSS.escape(filterValue) + '"]');
              if (cbCat) { cbCat.checked = false; }
              break;
            }
            case 'type': {
              var cbType = root.querySelector('input[name="type[]"][value="' + CSS.escape(filterValue) + '"]');
              if (cbType) { cbType.checked = false; }
              break;
            }
            case 'keywords': {
              root.querySelectorAll('[data-events-keywords]').forEach(function (kwEl) {
                kwEl.value = '';
              });
              break;
            }
            case 'start_date':
            case 'end_date': {
              var dateInp = root.querySelector('[name="' + filterType + '"]');
              if (dateInp) {
                dateInp.value = '';
                clearDateError(dateInp);
                var pw = dateInp.closest('[data-date-picker]');
                if (pw && pw._flatpickr) { pw._flatpickr.clear(); }
              }
              break;
            }
          }

          var state = readState();
          state.page = 0;
          fetchResults(state, true);
        });

        // Back / forward navigation.
        window.addEventListener('popstate', function () {
          restoreFromUrl();
          var state = readState();
          fetchResults(state, false);
        });

        // ── Initial hydration ────────────────────────────────────────────────
        // Sync current URL state and seed history.state so popstate works.
        var initialState = readState();
        history.replaceState({ eventsState: initialState }, '', location.href);
        updateAppliedChips(initialState);
        updateAccordionRowSelectedStates();
      });
    },
  };

})(jQuery, Drupal, once);
